import React, { useState } from 'react';
import api from '../api';
import PrivacyPolicy from '../components/PrivacyPolicy';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { requestPushPermission, refreshPushToken } from '../firebase';
import { User, Mail, Save, Shield, KeyRound, CheckCircle, AlertCircle, Bell } from 'lucide-react';

function ProfilePage({ user, onUserUpdate }) {
  const [fullName, setFullName] = useState(user.full_name || '');
  const [email, setEmail] = useState(user.email || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const pushSupported = 'Notification' in window && 'serviceWorker' in navigator;
  const [pushPermission, setPushPermission] = useState(() => pushSupported ? Notification.permission : 'unsupported');
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSuccess, setPushSuccess] = useState(false);
  const [pushError, setPushError] = useState('');

  const handlePushSubscribe = async () => {
    setPushLoading(true);
    setPushError('');
    setPushSuccess(false);
    try {
      let token;
      if (Notification.permission === 'granted') {
        token = await refreshPushToken();
      } else {
        token = await requestPushPermission();
      }
      const currentPerm = Notification.permission;
      setPushPermission(currentPerm);
      if (!token) {
        setPushError('Token nije dobiven. Dozvola je odbijena ili je korisnik otkazao zahtjev.');
        return;
      }
      await api.post('/push/subscribe', { token });
      localStorage.setItem('push_token', token);
      setPushSuccess(true);
      setTimeout(() => setPushSuccess(false), 4000);
    } catch (err) {
      setPushError(err.message || 'Nepoznata greška pri aktivaciji push notifikacija.');
    } finally {
      setPushLoading(false);
    }
  };

  const initials = (user.full_name || user.email || '?')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const colors = ['#0984e3','#6c5ce7','#00b894','#e17055','#fd79a8'];
  const avatarBg = colors[(user.email || '').charCodeAt(0) % colors.length];

  const handleSave = async () => {
    if (!fullName.trim()) return setError('Ime i prezime je obavezno.');
    if (!email.trim()) return setError('E-mail je obavezan.');
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const res = await api.put('/me', { full_name: fullName.trim(), email: email.trim() });
      setSuccess(true);
      onUserUpdate && onUserUpdate(res.data);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Greška pri spremanju.');
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = fullName !== (user.full_name || '') || email !== (user.email || '');

  return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>

      {/* Avatar kartica */}
      <div style={{ background: 'white', borderRadius: 16, padding: 28, marginBottom: 16, border: '1px solid #eef0f3', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: avatarBg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, margin: '0 auto 12px', letterSpacing: '-1px' }}>
          {initials}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1a2a4a', marginBottom: 4 }}>
          {user.full_name || 'Korisnik'}
        </div>
        <div style={{ fontSize: 16, color: '#b2bec3', marginBottom: 8 }}>{user.email}</div>
        <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, background: '#e8f4fd', color: '#0984e3' }}>
          {user.role}
        </span>
      </div>

      {/* Uredi podatke */}
      <div style={{ background: 'white', borderRadius: 16, padding: 24, marginBottom: 16, border: '1px solid #eef0f3', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#e8f4fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <User size={15} color="#0984e3" />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1a2a4a' }}>Osobni podaci</div>
            <div style={{ fontSize: 13, color: '#b2bec3' }}>Uredi svoje ime i e-mail adresu</div>
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <User size={13} color="#636e72" /> Ime i prezime
          </label>
          <input
            value={fullName}
            onChange={e => { setFullName(e.target.value); setError(''); setSuccess(false); }}
            placeholder="Upiši ime i prezime..."
          />
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Mail size={13} color="#636e72" /> E-mail adresa
          </label>
          <input
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(''); setSuccess(false); }}
            placeholder="Upiši e-mail adresu..."
          />
          <div style={{ fontSize: 13, color: '#b2bec3', marginTop: 4 }}>
            E-mail se koristi i za prijavu u aplikaciju.
          </div>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '8px 12px', background: '#fde8e8', borderRadius: 8, fontSize: 14, color: '#d63031' }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {success && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '8px 12px', background: '#d4f5ec', borderRadius: 8, fontSize: 14, color: '#00b894' }}>
            <CheckCircle size={14} /> Podaci uspješno spremljeni!
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={loading || !hasChanges}
          className="btn btn-primary"
          style={{ marginTop: 16, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#1a2a4a', opacity: (!hasChanges || loading) ? 0.5 : 1, cursor: (!hasChanges || loading) ? 'not-allowed' : 'pointer' }}
        >
          <Save size={14} /> {loading ? 'Spremanje...' : 'Spremi promjene'}
        </button>
      </div>

      {/* Sigurnost */}
      <div style={{ background: 'white', borderRadius: 16, padding: 24, marginBottom: 16, border: '1px solid #eef0f3', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fde8e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <KeyRound size={15} color="#d63031" />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1a2a4a' }}>Sigurnost</div>
            <div style={{ fontSize: 13, color: '#b2bec3' }}>Upravljaj lozinkom svog računa</div>
          </div>
        </div>

        <button
          onClick={() => setShowChangePassword(true)}
          className="btn btn-secondary"
          style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          <KeyRound size={14} /> Promijeni lozinku
        </button>
      </div>

      {/* Push notifikacije */}
      {pushSupported && (
        <div style={{ background: 'white', borderRadius: 16, padding: 24, marginBottom: 16, border: '1px solid #eef0f3', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bell size={15} color="#e17055" />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#1a2a4a' }}>Push notifikacije</div>
              <div style={{ fontSize: 13, color: '#b2bec3' }}>
                Dozvola:{' '}
                <span style={{ fontWeight: 600, color: pushPermission === 'granted' ? '#00b894' : pushPermission === 'denied' ? '#d63031' : '#636e72' }}>
                  {pushPermission === 'granted' ? 'Odobreno' : pushPermission === 'denied' ? 'Odbijeno' : 'Nije zatraženo'}
                </span>
              </div>
            </div>
          </div>

          {pushPermission === 'denied' ? (
            <div style={{ padding: '10px 14px', background: '#fde8e8', borderRadius: 8, fontSize: 14, color: '#d63031', lineHeight: 1.5 }}>
              Dozvola je odbijena u pregledniku. Da bi aktivirao push, idi u postavke preglednika → Site settings → Notifications i dopusti za ovu stranicu.
            </div>
          ) : (
            <button
              onClick={handlePushSubscribe}
              disabled={pushLoading}
              className="btn btn-secondary"
              style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: pushLoading ? 0.6 : 1, cursor: pushLoading ? 'not-allowed' : 'pointer' }}
            >
              <Bell size={14} />
              {pushLoading ? 'Aktivacija...' : pushPermission === 'granted' ? 'Osvježi push token' : 'Aktiviraj push notifikacije'}
            </button>
          )}

          {pushError && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 10, padding: '8px 12px', background: '#fde8e8', borderRadius: 8, fontSize: 13, color: '#d63031', wordBreak: 'break-word' }}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {pushError}
            </div>
          )}

          {pushSuccess && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '8px 12px', background: '#d4f5ec', borderRadius: 8, fontSize: 13, color: '#00b894' }}>
              <CheckCircle size={14} /> Push token osvježen i registriran!
            </div>
          )}
        </div>
      )}

      {/* Privatnost */}
      <div style={{ background: 'white', borderRadius: 16, padding: 24, border: '1px solid #eef0f3', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#e8f4fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={15} color="#0984e3" />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1a2a4a' }}>Privatnost</div>
            <div style={{ fontSize: 13, color: '#b2bec3' }}>Kako koristimo tvoje podatke</div>
          </div>
        </div>

        <button
          onClick={() => setShowPrivacy(true)}
          className="btn btn-secondary"
          style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          <Shield size={14} /> Politika privatnosti
        </button>
      </div>

      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}

export default ProfilePage;
